import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const PopupContext = createContext(null);

/**
 * PopupProvider 컴포넌트
 * 전역 팝업 상태를 관리하고 팝업 제어 함수를 제공
 */
export const PopupProvider = ({ children }) => {
  const { t } = useTranslation();
  const [popupState, setPopupState] = useState({
    isOpen: false,
    type: null, // 'text', 'textarea', 'thumbnail', 'thumbnail2', 'signature', 'stamp', 'programForm'
    config: null,
    resolvePromise: null,
  });

  /**
   * 팝업 열기
   * @param {string} type - 팝업 타입 ('text', 'textarea', 'thumbnail', 'thumbnail2', 'signature', 'stamp', 'programForm', 'programSelect')
   * @param {object} config - 팝업 설정
   * @param {string} config.title - 팝업 제목
   * @param {string} config.content - 팝업 내용
   * @param {string} config.label - 라벨 텍스트
   * @param {object} config.leftButton - 왼쪽 버튼 설정
   * @param {object} config.rightButton - 오른쪽 버튼 설정
   * @param {any} config.initialData - 초기 데이터
   * @param {string} config.displayType - 팝업 표시 방식 ('modal' | 'slide')
   * @param {string|number} config.width - 팝업 너비 (예: '1000px', '90vw', 1000)
   * @param {string|number} config.height - 팝업 높이 (예: '600px', '80vh', 600)
   * @param {string|number} config.maxWidth - 최대 너비 (예: '1400px', '95vw', 1400)
   * @param {string|number} config.maxHeight - 최대 높이 (예: '90vh', 800)
   * @param {string|number} config.minWidth - 최소 너비 (예: '600px', '50vw', 600)
   * @param {string|number} config.minHeight - 최소 높이 (예: '400px', '40vh', 400)
   * @param {boolean} config.closeOnBackdrop - 배경 클릭 시 닫기 (기본: false)
   * @returns {Promise} - 팝업이 닫힐 때 resolve되는 Promise
   */
  const openPopup = useCallback((type, config = {}) => {
    const cancelLabel = t('popup.common.cancel');
    const confirmLabel = t('popup.common.confirm');
    return new Promise((resolve) => {
      setPopupState({
        isOpen: true,
        type,
        config: {
          title: config.title || '',
          content: config.content || '',
          label: config.label || '',
          leftButton: config.leftButton || { text: cancelLabel, color: 'btn-secondary', submit: false },
          rightButton: config.rightButton || { text: confirmLabel, color: 'btn-primary', submit: true },
          initialData: config.initialData || null, // thumbnail/thumbnail2용 초기 데이터
          displayType: config.displayType || 'modal', // 'modal' | 'slide' - 팝업 표시 방식
          width: config.width || null, // 팝업 너비
          height: config.height || null, // 팝업 높이
          maxWidth: config.maxWidth || null, // 최대 너비
          maxHeight: config.maxHeight || null, // 최대 높이
          minWidth: config.minWidth || null, // 최소 너비
          minHeight: config.minHeight || null, // 최소 높이
          closeOnBackdrop: config.closeOnBackdrop === true,
          ...config,
        },
        resolvePromise: resolve,
      });
    });
  }, [t]);

  /**
   * 팝업 닫기
   * @param {any} result - 팝업 결과값 (Promise resolve에 전달됨)
   */
  const closePopup = useCallback((result = null) => {
    setPopupState((prev) => {
      if (prev.resolvePromise) {
        prev.resolvePromise(result);
      }
      return {
        isOpen: false,
        type: null,
        config: null,
        resolvePromise: null,
      };
    });
  }, []);

  /**
   * 텍스트 팝업 열기 (간단한 확인/취소)
   */
  const openTextPopup = useCallback((config = {}) => {
    return openPopup('text', config);
  }, [openPopup]);

  /**
   * 공통 알림 팝업 (확인 버튼만, alert 대체)
   * @param {object} config - { title?, content }
   * @returns {Promise<{ submit: true }>}
   */
  const openAlert = useCallback((config = {}) => {
    return openPopup('text', {
      title: config.title ?? t('components.common.alert'),
      content: config.content ?? '',
      leftButton: null,
      rightButton: { text: t('popup.common.confirm'), color: 'btn-primary', submit: true },
    });
  }, [openPopup, t]);

  /**
   * 공통 확인 팝업 (취소/확인, confirm 대체)
   * @param {object} config - { title?, content? }
   * @returns {Promise<{ submit: boolean }>} - 확인 시 { submit: true }, 취소/ESC 시 { submit: false }
   */
  const openConfirm = useCallback((config = {}) => {
    return openPopup('text', {
      title: config.title ?? t('popup.common.confirm'),
      content: config.content ?? '',
      leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
      rightButton: { text: t('popup.common.confirm'), color: 'btn-primary', submit: true },
    });
  }, [openPopup, t]);

  /**
   * 텍스트 영역 팝업 열기 (textarea 포함)
   */
  const openTextareaPopup = useCallback((config = {}) => {
    return openPopup('textarea', config);
  }, [openPopup]);

  /**
   * 썸네일 팝업 열기 (이미지 업로드 + 입력 필드)
   */
  const openThumbnailPopup = useCallback((config = {}) => {
    return openPopup('thumbnail', config);
  }, [openPopup]);

  /**
   * 썸네일2 팝업 열기 (이미지 업로드 + CKEditor)
   */
  const openThumbnail2Popup = useCallback((config = {}) => {
    return openPopup('thumbnail2', config);
  }, [openPopup]);

  /**
   * 서명 팝업 열기 (HTML5 Canvas 기반 전자서명)
   */
  const openSignaturePopup = useCallback((config = {}) => {
    return openPopup('signature', config);
  }, [openPopup]);

  /**
   * 직인 이미지 팝업 열기 (이미지 업로드)
   */
  const openStampPopup = useCallback((config = {}) => {
    return openPopup('stamp', config);
  }, [openPopup]);


  /**
   * 프로그램 폼 팝업 열기 (프로그램 등록/수정)
   */
  const openProgramFormPopup = useCallback((config = {}) => {
    return openPopup('programForm', config);
  }, [openPopup]);

  /**
   * 프로그램 선택 팝업 열기
   */
  const openProgramSelectPopup = useCallback((config = {}) => {
    return openPopup('programSelect', config);
  }, [openPopup]);

  /**
   * 전문가 / 기관 정보 팝업 열기 (다중선택)
   */
  const openExpertOrgSelectPopup = useCallback((config = {}) => {
    return openPopup('expertOrgSelect', config);
  }, [openPopup]);

  /** Alliance Space 초대 제안 (연구 협력 제안 수락자 대상). config: linkedTaskTitle, linkedTaskMinistry?, linkedTaskAgency?. resolve: { submit, spaceUid?, messageText? } */
  const openAllianceSpaceInviteProposalPopup = useCallback((config = {}) => {
    return openPopup('allianceSpaceInviteProposal', config);
  }, [openPopup]);

  /**
   * 기술 수요 지원자 관리 팝업 열기
   */
  const openTechDemandApplicantsPopup = useCallback((config = {}) => {
    return openPopup('techDemandApplicants', config);
  }, [openPopup]);

  /**
   * 정부부처/전문기관별 보기 팝업 열기 (체크 다중선택)
   * config.initialSelectedMinistries, config.initialSelectedAgencies (string[])
   * resolve 시 { submit, selectedMinistries, selectedAgencies }
   */
  const openDeptAgencySelectPopup = useCallback((config = {}) => {
    return openPopup('deptAgencySelect', config);
  }, [openPopup]);

  /**
   * 컨소시엄(협업 인원) 확정 팝업 열기
   * config.initialConsortium: { organization, person }[]
   * resolve 시 { submit, consortium }
   */
  const openConsortiumSelectPopup = useCallback((config = {}) => {
    return openPopup('consortiumSelect', config);
  }, [openPopup]);

  /**
   * 기관 담당자 추가/편집 팝업
   * config.mode: 'add' | 'edit', config.initialData: 편집 시 데이터
   * resolve 시 { submit, formData }
   */
  const openOrgManagerFormPopup = useCallback((config = {}) => {
    return openPopup('orgManagerForm', config);
  }, [openPopup]);

  /** 멤버 역할 변경 팝업. initialData: { member, isOnlyOwner }. resolve 시 { submit, selectedRole } */
  const openMemberRoleChangePopup = useCallback((config = {}) => {
    return openPopup('memberRoleChange', config);
  }, [openPopup]);

  /** 컨소시엄 멤버 정보 수정(역할·일시 중지). initialData: { rowKind, member?, archiveItem?, isOnlyOwner, canChangeRole, canManageAll, suspendEligible } */
  const openConsortiumMemberEditPopup = useCallback((config = {}) => {
    return openPopup('consortiumMemberEdit', config);
  }, [openPopup]);

  /** Alliance Space 역할 관리(역할명·설명·권한). initialData: { roleRow, spaceId?, boardTrees?, milestoneTree?, dynamicMenus?, initialPermissions? } — spaceId 있으면 게시판 트리·마일스톤(/milestones) 자동 조회(전달된 트리는 생략). resolve: { submit, roleName, description, permissions, roleRow } */
  const openConsortiumRoleManagePopup = useCallback((config = {}) => {
    return openPopup('consortiumRoleManage', config);
  }, [openPopup]);

  /** 멤버 임시 권한 설정 팝업. initialData: { member }. resolve 시 { submit, checks, start, end } */
  const openMemberTempPermissionPopup = useCallback((config = {}) => {
    return openPopup('memberTempPermission', config);
  }, [openPopup]);

  /** 멤버 퇴장 처리 3단계 팝업. initialData: { member, getTransferCandidates, getNewOwnerCandidates, allMembersWithOrg }. resolve 시 { submit, newOwnerId, transferToId, fileHandling, reason } */
  const openMemberRemovePopup = useCallback((config = {}) => {
    return openPopup('memberRemove', config);
  }, [openPopup]);

  /**
   * 비밀번호 재설정 (로그인 화면 등). initialData: { initialUserId? }
   * 성공 시 resolve: { submit: true, passwordChanged: true }
   */
  const openForgotPasswordPopup = useCallback((config = {}) => {
    return openPopup('forgotPassword', config);
  }, [openPopup]);

  /** 마이페이지 회원 탈퇴. initialData: { userUid }. 성공 시 resolve: { submit: true, withdrawn: true, deleteMode?, permanent? } */
  const openAccountWithdrawPopup = useCallback((config = {}) => {
    return openPopup('accountWithdraw', config);
  }, [openPopup]);

  /** SMTP 설정 등록/수정 팝업. config.initialData: 수정 시 row, 없으면 신규. resolve: { submit, data?, activated?, deleted? } */
  const openSmtpSettingsFormPopup = useCallback((config = {}) => {
    return openPopup('smtpSettingsForm', config);
  }, [openPopup]);

  const value = {
    popupState,
    openPopup,
    closePopup,
    openTextPopup,
    openAlert,
    openConfirm,
    openTextareaPopup,
    openThumbnailPopup,
    openThumbnail2Popup,
    openSignaturePopup,
    openStampPopup,
    openProgramFormPopup,
    openProgramSelectPopup,
    openExpertOrgSelectPopup,
    openAllianceSpaceInviteProposalPopup,
    openTechDemandApplicantsPopup,
    openDeptAgencySelectPopup,
    openConsortiumSelectPopup,
    openOrgManagerFormPopup,
    openMemberRoleChangePopup,
    openConsortiumMemberEditPopup,
    openConsortiumRoleManagePopup,
    openMemberTempPermissionPopup,
    openMemberRemovePopup,
    openForgotPasswordPopup,
    openAccountWithdrawPopup,
    openSmtpSettingsFormPopup,
  };

  return <PopupContext.Provider value={value}>{children}</PopupContext.Provider>;
};

/**
 * usePopup Hook
 * 팝업 제어 함수를 제공하는 커스텀 Hook
 */
export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

export default PopupContext;
